/**
 * Lista de pessoas (pacientes com consentimento ou clientes cadastrados).
 * Telefone sempre visível. Consentimento só na lista de clientes cadastrados.
 */
import { memo, useState } from "react";
import {
  Button,
  Checkbox,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import type { Patient } from "fhir/r5";
import { PrimaryButton } from "@/components/common";
import { useFormatter } from "@/utils/useFormatter";
import {
  statsTableCellTextSx,
  statsTableContainerSx,
  statsTableHeadCellSx,
  statsTableSx,
  statsTableTruncatedCellSx,
} from "@/pages/adminInsights/utils/statsTableStyles";
import type { EcommercePeopleRow } from "src-next/modules/ecommerce";
import { formatEcommercePhoneDisplay } from "src-next/modules/ecommerce/helpers/formatEcommercePhoneDisplay";

const ROWS_PER_PAGE_OPTIONS = [8, 16, 32];

interface EcommercePeopleTableProps {
  rows: EcommercePeopleRow[];
  selectedKeys: string[];
  onToggleRow: (key: string) => void;
  page: number;
  onPageChange: (page: number) => void;
  onManage?: (patient: Patient) => void;
  /** Só clientes cadastrados: coluna de consentimento + ação WhatsApp. */
  showConsentColumn?: boolean;
  onSendWhatsApp?: (row: EcommercePeopleRow) => void;
  whatsAppDisabled?: boolean;
}

export default memo(function EcommercePeopleTable({
  rows,
  selectedKeys,
  onToggleRow,
  page,
  onPageChange,
  onManage,
  showConsentColumn = false,
  onSendWhatsApp,
  whatsAppDisabled = false,
}: EcommercePeopleTableProps) {
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const paginated = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Stack spacing={0}>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ ...statsTableContainerSx, borderColor: "primary.light" }}
      >
        <Table size="medium" sx={statsTableSx}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" width={48} sx={statsTableHeadCellSx} />
              <TableCell sx={{ ...statsTableHeadCellSx, width: "32%" }}>
                Nome
              </TableCell>
              <TableCell sx={{ ...statsTableHeadCellSx, width: 140 }}>
                CPF
              </TableCell>
              <TableCell sx={{ ...statsTableHeadCellSx, width: 150 }}>
                Telefone
              </TableCell>
              {showConsentColumn ? (
                <TableCell sx={{ ...statsTableHeadCellSx, width: 140 }}>
                  Consentimento
                </TableCell>
              ) : null}
              <TableCell
                align="center"
                sx={{ ...statsTableHeadCellSx, width: 160 }}
              >
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.map((row) => {
              const selected = selectedKeys.includes(row.key);
              const phoneLabel = row.phone
                ? formatEcommercePhoneDisplay(row.phone)
                : "Não informado";
              return (
                <TableRow key={row.key} hover selected={selected}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={selected}
                      onChange={() => onToggleRow(row.key)}
                      inputProps={{ "aria-label": `Selecionar ${row.name}` }}
                    />
                  </TableCell>
                  <TableCell sx={statsTableTruncatedCellSx}>
                    <Tooltip title={row.name}>
                      <Typography fontWeight={600} sx={statsTableCellTextSx} noWrap>
                        {row.name}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={statsTableTruncatedCellSx}>
                    <Typography color="text.secondary" sx={statsTableCellTextSx} noWrap>
                      {useFormatter.formatCPF(row.cpf) || "Não informado"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={statsTableTruncatedCellSx}>
                    <Typography color="text.secondary" sx={statsTableCellTextSx} noWrap>
                      {phoneLabel}
                    </Typography>
                  </TableCell>
                  {showConsentColumn ? (
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.hasConsent ? "Sim" : "Não"}
                        color={row.hasConsent ? "success" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                  ) : null}
                  <TableCell align="center">
                    {showConsentColumn && onSendWhatsApp ? (
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<WhatsAppIcon />}
                        disabled={whatsAppDisabled}
                        onClick={() => onSendWhatsApp(row)}
                        sx={{ textTransform: "none", fontWeight: 600, height: 36 }}
                      >
                        WhatsApp
                      </Button>
                    ) : row.canManage && row.patient && onManage ? (
                      <Tooltip title="Verificar documentos e liberar compra">
                        <span>
                          <PrimaryButton
                            width="fit-content"
                            height="36px"
                            startIcon={<DescriptionOutlinedIcon fontSize="small" />}
                            onClick={() => onManage(row.patient as Patient)}
                          >
                            Gerenciar
                          </PrimaryButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_, nextPage) => onPageChange(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          onPageChange(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        labelRowsPerPage="Linhas por página"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
      />
    </Stack>
  );
});
